import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { usePublicStats } from "../components/DynamicGrantCount";
import {
  Lightning, CheckCircle, ArrowRight, Buildings, Receipt, Bank, Users,
  Scales, Storefront, CurrencyDollar, FilePlus, EnvelopeSimple, MapPin, ListChecks,
  Question
} from "@phosphor-icons/react";

/**
 * Public SEO landing page — /start-a-nonprofit-in-sc
 *
 * Purpose:
 *   - Rank for "how to start a nonprofit in South Carolina" search queries
 *   - Capture email leads from aspiring SC nonprofit founders
 *   - Funnel qualified pre-501(c)(3) founders into the FlashGrants 14-day trial
 *
 * No auth required. Lead capture posts to /api/leads/nonprofit-roadmap.
 */

const STEPS = [
  { n: 1, t: "Form a SC corporation", icon: Buildings,
    body: "File Articles of Incorporation with the SC Secretary of State (Charity & Nonprofit division). Cost: $25. Online at sos.sc.gov. You'll need a name search, registered agent, and 3+ incorporators." },
  { n: 2, t: "Get an EIN from the IRS", icon: Receipt,
    body: "Apply for a free Employer Identification Number at irs.gov/businesses/employer-identification-number. Takes about 15 minutes online. You'll need this for everything that comes next." },
  { n: 3, t: "Adopt bylaws + a conflict-of-interest policy", icon: Scales,
    body: "Required for the IRS to grant tax exemption. The IRS publishes sample bylaws and a sample conflict-of-interest policy in Appendix A of Publication 557. Use them as a starting template." },
  { n: 4, t: "Hold an organizational board meeting", icon: Users,
    body: "Adopt the bylaws, elect officers (President, Secretary, Treasurer at minimum), and record minutes. Most SC nonprofits aim for 3-5 board members at this stage — you'll be expected to have an independent board." },
  { n: 5, t: "File IRS Form 1023-EZ (most SC startups)", icon: FilePlus,
    body: "If projected annual revenue is under $50,000 and assets under $250,000, you qualify for the simpler 1023-EZ. Cost: $275. Average wait: 2-4 months. Larger orgs file the full Form 1023 ($600, 6-12 month wait)." },
  { n: 6, t: "Register with SC Secretary of State (Charities Division)", icon: ListChecks,
    body: "Required for any SC nonprofit that solicits donations. Annual filing through sos.sc.gov. Cost: $50 first time, $50/year renewal. Don't skip this — it's a misdemeanor to fundraise in SC without registering." },
  { n: 7, t: "Apply for SC sales tax exemption (if applicable)", icon: CurrencyDollar,
    body: "Once you have your IRS determination letter, file Form ST-387 with SC Department of Revenue for sales tax exemption on purchases. Most religious, educational, and charitable orgs qualify." },
  { n: 8, t: "Open a nonprofit bank account", icon: Bank,
    body: "Take your EIN letter, bylaws, board resolution authorizing the account, and IRS determination letter (or your 1023-EZ submission receipt) to a SC bank. SouthState, First Bank, and local credit unions are nonprofit-friendly." },
];

const COMMON_MISTAKES = [
  "Filing for 501(c)(3) before you have a real program or measurable activity. The IRS rejects applications that look like 'shell' nonprofits with no actual work happening.",
  "Naming family members as your only board. The IRS wants to see an INDEPENDENT board with majority unrelated members. Minimum 3 unrelated members is the safe target.",
  "Skipping the SC Charities Division registration. It's required to solicit donations in SC, and missing it can disqualify you from grants.",
  "Confusing 'fiscal sponsorship' with 'fiscal agent.' Fiscal sponsorship (where a 501(c)(3) hosts your project) is legitimate. Fiscal 'agent' arrangements often aren't.",
  "Trying to apply for grants before your determination letter arrives. Most grants require an active 501(c)(3). However, some (listed in FlashGrants) ARE open to fiscally-sponsored projects and pre-501(c)(3) groups.",
];

const FAQ = [
  { q: "How long does the whole process take?", a: "Realistically: 3-6 months from filing your SC Articles of Incorporation to receiving your IRS determination letter. The Form 1023-EZ approval averages 2-4 months once submitted." },
  { q: "What does it cost total?", a: "Budget approximately $350-$400 in mandatory fees ($25 SC incorporation + $275 IRS 1023-EZ + $50 SC charity registration). Add $50-$200 if you hire an attorney to review bylaws (recommended but not required)." },
  { q: "Can I apply for grants while I'm waiting?", a: "Yes — for some grants. Players Philanthropy Fund and other fiscal sponsors can host your project so you can apply for grants under their 501(c)(3) status. FlashGrants tags which grants are open to fiscally-sponsored projects." },
  { q: "Do I need an attorney?", a: "Not legally required for 1023-EZ. Recommended for bylaws review, especially if your nonprofit will have paid staff, significant assets, or unusual governance. A 1-hour consultation with a SC nonprofit attorney typically costs $200-$400." },
  { q: "What if my nonprofit will earn revenue (fees, sales)?", a: "Revenue is allowed and common, but it must be 'substantially related' to your mission, or it becomes Unrelated Business Income (UBI) and gets taxed. Examples: a nonprofit theater selling tickets = related; the same theater selling t-shirts unrelated to its mission = potentially UBI. Talk to an SC nonprofit CPA before launching revenue streams." },
  { q: "Should I do a 'hybrid' LLC + 501(c)(3) structure?", a: "If you're running a for-profit business alongside a charitable mission (e.g., a jazz lounge LLC + a music education nonprofit), yes — and the structure is legitimate. The two entities must be legally separate with separate EINs, separate bank accounts, separate boards, and arms-length agreements. We're building a Hybrid Model Playbook for SC entrepreneurs in this exact situation." },
];

const StartANonprofitInSC = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [orgIdea, setOrgIdea] = useState("");
  const [county, setCounty] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const publicStats = usePublicStats();
  const grantCount = publicStats?.total_active || 100;
  const scoutCount = publicStats?.corporate_scouts || 30;

  useEffect(() => {
    // SEO: set the title + meta description on mount; refresh when grantCount loads
    document.title = "How to Start a Nonprofit in South Carolina — The Complete 8-Step Guide | FlashGrants";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content",
      `Free 8-step roadmap to start a 501(c)(3) nonprofit in South Carolina. Real costs, real timelines, real SC-specific filings — plus ${grantCount} verified SC grants to fund your mission. No fluff.`
    );
  }, [grantCount]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setErrorMsg("Please enter a valid email.");
      setStatus("error");
      return;
    }
    setStatus("submitting");
    setErrorMsg("");
    try {
      await axios.post(`${API}/leads/nonprofit-roadmap`, {
        name, email, org_idea: orgIdea, county, source: "start-a-nonprofit-in-sc",
      });
      setStatus("success");
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || "Something went wrong — please try again.");
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="start-nonprofit-sc-page">
      {/* Header strip */}
      <header className="border-b border-zinc-800/60 sticky top-0 z-30 bg-zinc-950/80 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <Lightning weight="fill" className="w-5 h-5 text-emerald-400 group-hover:text-emerald-300 transition" />
            <span className="text-sm font-bold tracking-tight">FlashGrants</span>
          </Link>
          <Link
            to="/"
            className="text-xs font-semibold text-zinc-400 hover:text-emerald-400 transition"
            data-testid="nav-back-to-home"
          >
            Back to flash-grants.com →
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 sm:px-6 pt-16 pb-12 sm:pt-24 sm:pb-16 border-b border-zinc-800/60">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-mono uppercase tracking-widest mb-6">
            <MapPin weight="fill" className="w-3.5 h-3.5" />
            South Carolina Specific · No fluff
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
            How to start a nonprofit <span className="text-emerald-400">in South Carolina</span> — without spending $2,000 on a consultant.
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 leading-relaxed mb-2">
            8 steps. Real SC filings. Real costs ($350–$400 total). Realistic timeline (3–6 months).
          </p>
          <p className="text-base sm:text-lg text-zinc-400 leading-relaxed">
            We built this guide for the people we keep meeting at our Thursday community meetings — passionate founders who have a clear mission and zero idea where to start with the paperwork. If that's you, read on.
          </p>
        </div>
      </section>

      {/* Quick at-a-glance card */}
      <section className="px-4 sm:px-6 py-12">
        <div className="max-w-3xl mx-auto p-6 sm:p-8 rounded-sm bg-zinc-900/60 border border-zinc-800">
          <p className="text-xs uppercase tracking-widest text-emerald-400 font-mono mb-4">TLDR — what you're getting into</p>
          <div className="grid sm:grid-cols-3 gap-6">
            <div>
              <p className="text-3xl font-bold text-white">~$350</p>
              <p className="text-sm text-zinc-400 mt-1">Total mandatory fees (SC + IRS)</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">3–6 months</p>
              <p className="text-sm text-zinc-400 mt-1">From start to IRS determination letter</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">8 steps</p>
              <p className="text-sm text-zinc-400 mt-1">All listed below, in order, with sources</p>
            </div>
          </div>
        </div>
      </section>

      {/* The 8 steps */}
      <section className="px-4 sm:px-6 pb-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8 tracking-tight">The 8-step roadmap</h2>
          <ol className="space-y-6">
            {STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <li
                  key={s.n}
                  className="p-5 sm:p-6 rounded-sm bg-zinc-900/40 border border-zinc-800 hover:border-emerald-500/40 transition"
                  data-testid={`step-${s.n}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-sm bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center font-mono font-bold text-emerald-400">
                      {s.n}
                    </div>
                    <div className="flex-1">
                      <h3 className="flex items-center gap-2 font-bold text-white text-lg mb-2">
                        <Icon weight="fill" className="w-5 h-5 text-emerald-400" />
                        {s.t}
                      </h3>
                      <p className="text-sm text-zinc-400 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* Common mistakes */}
      <section className="px-4 sm:px-6 pb-16 border-t border-zinc-800/60 pt-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2 tracking-tight">5 mistakes that kill SC nonprofit applications</h2>
          <p className="text-sm text-zinc-500 mb-8">Most of these come from going too fast or skipping a step. Don't.</p>
          <ul className="space-y-4">
            {COMMON_MISTAKES.map((m, i) => (
              <li
                key={i}
                className="flex items-start gap-3 p-4 rounded-sm bg-zinc-900/30 border border-zinc-800/60"
                data-testid={`mistake-${i}`}
              >
                <span className="flex-shrink-0 text-amber-400 font-mono text-xs mt-1">[{i + 1}]</span>
                <p className="text-sm text-zinc-300 leading-relaxed">{m}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 sm:px-6 pb-16 border-t border-zinc-800/60 pt-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8 tracking-tight">FAQ</h2>
          <div className="space-y-5">
            {FAQ.map((f, i) => (
              <div key={i} className="p-5 rounded-sm bg-zinc-900/40 border border-zinc-800" data-testid={`faq-${i}`}>
                <p className="flex items-start gap-2 font-semibold text-white mb-2">
                  <Question weight="fill" className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  {f.q}
                </p>
                <p className="text-sm text-zinc-400 leading-relaxed pl-7">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead capture */}
      <section className="px-4 sm:px-6 pb-20 border-t border-zinc-800/60 pt-16" id="get-the-roadmap">
        <div className="max-w-2xl mx-auto p-6 sm:p-10 rounded-sm bg-gradient-to-br from-emerald-500/10 via-zinc-900/40 to-zinc-900/60 border border-emerald-500/30">
          <p className="text-xs uppercase tracking-widest text-emerald-400 font-mono mb-3 flex items-center gap-2">
            <EnvelopeSimple weight="fill" className="w-3.5 h-3.5" />
            Free — get the editable templates
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 tracking-tight">
            Want this as a printable PDF + editable Word templates?
          </h2>
          <p className="text-sm text-zinc-400 leading-relaxed mb-6">
            Drop your email and we'll send you the full roadmap, plus sample bylaws, a conflict-of-interest policy template, and a board meeting minutes template. All free. No spam, ever — just one email with the toolkit, and the occasional update when SC adds a new grant we think you'd qualify for.
          </p>
          {status === "success" ? (
            <div
              className="flex items-start gap-3 p-4 rounded-sm bg-emerald-500/10 border border-emerald-500/40"
              data-testid="lead-success"
            >
              <CheckCircle weight="fill" className="w-5 h-5 text-emerald-400 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-emerald-300">You're on the list.</p>
                <p className="text-xs text-zinc-400 mt-1">
                  Check your inbox shortly. In the meantime — if you want to see the {grantCount} SC grants we track, including the ones open to pre-501(c)(3) projects through fiscal sponsorship —{" "}
                  <Link to="/grants" className="text-emerald-400 hover:text-emerald-300 underline">browse them here</Link>.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/60"
                  data-testid="lead-input-name"
                />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email *"
                  className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/60"
                  data-testid="lead-input-email"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={county}
                  onChange={(e) => setCounty(e.target.value)}
                  placeholder="SC County (optional)"
                  className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/60"
                  data-testid="lead-input-county"
                />
                <input
                  type="text"
                  value={orgIdea}
                  onChange={(e) => setOrgIdea(e.target.value)}
                  placeholder="One-line nonprofit idea (optional)"
                  className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/60"
                  data-testid="lead-input-idea"
                />
              </div>
              {status === "error" && (
                <p className="text-xs text-red-400" data-testid="lead-error">{errorMsg}</p>
              )}
              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed text-black rounded-sm text-sm font-bold uppercase tracking-widest transition"
                data-testid="lead-submit-btn"
              >
                {status === "submitting" ? "Sending…" : "Send me the roadmap"}
                <ArrowRight weight="bold" className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 sm:px-6 pb-24 border-t border-zinc-800/60 pt-16">
        <div className="max-w-3xl mx-auto text-center">
          <Storefront weight="fill" className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 tracking-tight">
            Already started forming yours? Find the grants you'll need.
          </h2>
          <p className="text-sm text-zinc-400 leading-relaxed mb-8 max-w-2xl mx-auto">
            FlashGrants tracks {grantCount} verified South Carolina grants and {scoutCount} SC corporate funders — including the ones open to pre-501(c)(3) projects through fiscal sponsorship. Free 14-day trial. No auto-charge.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-emerald-500/60 rounded-sm text-sm font-bold uppercase tracking-widest text-white transition"
            data-testid="cta-trial-btn"
          >
            <Lightning weight="fill" className="w-4 h-4 text-emerald-400" />
            Start free trial — see all {grantCount} grants
            <ArrowRight weight="bold" className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800/60 py-8 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center text-xs text-zinc-500">
          <p>
            FlashGrants is a private SC company helping nonprofits and small businesses find micro-grants. This page is for informational purposes only — not legal or tax advice. Consult a SC nonprofit attorney or CPA for advice specific to your situation.
          </p>
          <p className="mt-3">
            Built in South Carolina, by South Carolinians, for South Carolinians.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default StartANonprofitInSC;
